import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Star, Trophy, Users, MessageSquareQuote, ToggleLeft, ToggleRight, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { FestivalCreationHeader } from '../components/FestivalCreationHeader';

export default function CreateFestivalExperiencePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isSaving, setIsSaving] = useState(false);

  const [isFirstEdition, setIsFirstEdition] = useState(false);
  const [editionNumber, setEditionNumber] = useState('');
  const [pastAudience, setPastAudience] = useState('');
  const [historyDescription, setHistoryDescription] = useState('');
  const [organizerTestimonial, setOrganizerTestimonial] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      const activeId = sessionStorage.getItem('activeFestivalId') || user.uid;
      try {
        const snap = await getDoc(doc(db, 'festivals', activeId));
        if (snap.exists()) {
          const d = snap.data();
          setIsFirstEdition(d.isFirstEdition ?? false);
          setEditionNumber(d.editionNumber?.toString() ?? '');
          setPastAudience(d.pastAudience ?? '');
          setHistoryDescription(d.historyDescription ?? '');
          setOrganizerTestimonial(d.organizerTestimonial ?? '');
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchData();
  }, [user]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setIsSaving(true);
    const activeId = sessionStorage.getItem('activeFestivalId') || user.uid;
    try {
      await setDoc(doc(db, 'festivals', activeId), {
        isFirstEdition,
        editionNumber: isFirstEdition ? 1 : (parseInt(editionNumber) || null),
        pastAudience: isFirstEdition ? '' : pastAudience,
        historyDescription: isFirstEdition ? '' : historyDescription,
        organizerTestimonial: isFirstEdition ? '' : organizerTestimonial,
      }, { merge: true });
      navigate('/create-festival/step-potential');
    } catch (err) {
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white font-sans relative overflow-x-hidden pb-32">
      <FestivalCreationHeader onBack={() => navigate(-1)} />

      <div className="max-w-2xl mx-auto px-5 pt-12 pb-8">
        {/* Title */}
        <div className="mb-10">
          <p className="text-xs font-black uppercase tracking-[0.2em] text-[#FF5C00] mb-3">Étape 3 / 7</p>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight leading-[1.05] mb-4">
            Votre expérience
          </h1>
          <p className="text-gray-400 text-lg leading-relaxed">
            Ces informations rassurent les exposants et augmentent votre taux de candidature.
          </p>
        </div>

        <form onSubmit={handleSave} className="space-y-6">

          {/* Toggle 1ère édition */}
          <div className="bg-white/5 border border-white/10 rounded-[24px] p-6">
            <button
              type="button"
              onClick={() => setIsFirstEdition(v => !v)}
              className="w-full flex items-center justify-between gap-4"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#FF5C00]/10 flex items-center justify-center shrink-0">
                  <Sparkles className="w-5 h-5 text-[#FF5C00]" />
                </div>
                <div className="text-left">
                  <p className="font-bold text-white">C'est la 1ère édition de ce festival</p>
                  <p className="text-sm text-gray-500">Tout le monde commence quelque part 🎉</p>
                </div>
              </div>
              {isFirstEdition
                ? <ToggleRight className="w-8 h-8 text-[#FF5C00] shrink-0" />
                : <ToggleLeft className="w-8 h-8 text-gray-600 shrink-0" />
              }
            </button>

            {isFirstEdition && (
              <div className="mt-5 p-4 bg-[#FF5C00]/5 border border-[#FF5C00]/20 rounded-2xl">
                <p className="text-sm text-[#FF5C00] font-bold">Première édition activée</p>
                <p className="text-xs text-gray-400 mt-1">
                  Votre fiche affichera "1ère édition" et mettra en avant le côté pionnier de votre festival. Les exposants adorent être les premiers !
                </p>
              </div>
            )}
          </div>

          {/* Champs expérience — masqués si 1ère édition */}
          {!isFirstEdition && (
            <>
              {/* Numéro d'édition */}
              <div className="bg-white/5 border border-white/10 rounded-[24px] p-6">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center shrink-0">
                    <Trophy className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="font-bold text-white">Numéro d'édition</p>
                    <p className="text-sm text-gray-500">Quelle édition est-ce ?</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min={2}
                    max={100}
                    value={editionNumber}
                    onChange={e => setEditionNumber(e.target.value)}
                    placeholder="Ex : 5"
                    className="w-32 bg-black/40 border border-white/10 rounded-2xl px-4 py-3 text-white text-xl font-black focus:outline-none focus:border-[#FF5C00] transition-colors text-center"
                  />
                  <span className="text-gray-400 text-lg font-bold">ème édition</span>
                </div>
              </div>

              {/* Affluence passée */}
              <div className="bg-white/5 border border-white/10 rounded-[24px] p-6">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
                    <Users className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="font-bold text-white">Affluence des éditions précédentes</p>
                    <p className="text-sm text-gray-500">Donnez un ordre de grandeur</p>
                  </div>
                </div>
                <input
                  type="text"
                  value={pastAudience}
                  onChange={e => setPastAudience(e.target.value)}
                  placeholder="Ex : 8 000 visiteurs en 2024, 12 000 en 2025..."
                  className="w-full bg-black/40 border border-white/10 rounded-2xl px-4 py-3 text-white focus:outline-none focus:border-[#FF5C00] transition-colors"
                />
              </div>

              {/* Histoire / description des éditions passées */}
              <div className="bg-white/5 border border-white/10 rounded-[24px] p-6">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0">
                    <Star className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="font-bold text-white">Histoire du festival</p>
                    <p className="text-sm text-gray-500">Points forts, ambiance, ce qui vous distingue</p>
                  </div>
                </div>
                <textarea
                  value={historyDescription}
                  onChange={e => setHistoryDescription(e.target.value)}
                  placeholder="Racontez l'histoire de votre festival, ses points forts, l'ambiance unique que vous créez..."
                  rows={5}
                  className="w-full bg-black/40 border border-white/10 rounded-2xl px-4 py-3 text-white focus:outline-none focus:border-[#FF5C00] transition-colors resize-none"
                />
                <p className="text-xs text-gray-600 mt-2">{historyDescription.length} / 600 caractères</p>
              </div>

              {/* Témoignage exposant */}
              <div className="bg-white/5 border border-white/10 rounded-[24px] p-6">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                    <MessageSquareQuote className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <p className="font-bold text-white">Témoignage d'un exposant
                      <span className="ml-2 text-[9px] font-black uppercase tracking-widest text-blue-400 bg-blue-400/10 border border-blue-400/20 px-1.5 py-0.5 rounded-full align-middle">+Score</span>
                    </p>
                    <p className="text-sm text-gray-500">Un avis positif rassure fortement les candidats</p>
                  </div>
                </div>
                <textarea
                  value={organizerTestimonial}
                  onChange={e => setOrganizerTestimonial(e.target.value)}
                  placeholder={`"Super organisation, on revient chaque année !" — Le Stand de Marie`}
                  rows={3}
                  className="w-full bg-black/40 border border-white/10 rounded-2xl px-4 py-3 text-white focus:outline-none focus:border-[#FF5C00] transition-colors resize-none italic"
                />
              </div>
            </>
          )}

          {/* Bottom nav */}
          <div className="bg-white/5 border border-white/10 rounded-[24px] md:rounded-3xl p-6 md:p-8 mt-6">
            <div className="flex flex-col md:flex-row items-center gap-4">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="w-full md:w-auto px-8 py-4 rounded-xl md:rounded-2xl border border-white/10 text-white font-bold hover:bg-white/10 transition-all text-sm"
              >
                Précédent
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="w-full md:flex-1 bg-white text-black font-black py-4 px-8 rounded-xl md:rounded-2xl hover:bg-[#ff5e00] hover:text-white transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2 text-sm uppercase tracking-widest"
              >
                {isSaving ? 'Enregistrement...' : 'Continuer'}
                {!isSaving && <ArrowRight className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-gray-600 text-center mt-4">Toutes les informations peuvent être modifiées après publication.</p>
          </div>

        </form>
      </div>
    </div>
  );
}
